import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Gem, Sparkles } from 'lucide-react'
import confetti from 'canvas-confetti'
import audioService from '../../../services/audioService'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'

const RoyaleWinnerCard = ({
  winner,
  winTicket,
  winAmount,
  winPercent,
  isCurrentUser,
  isParticipant,
  skipEffects = false
}) => {
  if (!winner) return null

  const { t } = useTranslation()
  const isWinnerUser = Boolean(isCurrentUser || winner?.you)
  const isRoundParticipant = Boolean(isParticipant)

  useEffect(() => {
    if (skipEffects) return undefined

    if (isRoundParticipant) {
      hapticNotify(isWinnerUser ? 'success' : 'error')
      if (isWinnerUser) {
        hapticImpact('heavy')
      }
    }

    if (isWinnerUser) {
      audioService.play('royaleWin')
    } else if (isRoundParticipant) {
      audioService.play('upgraderFail')
    } else {
      audioService.play('upgraderWin')
    }

    if (!isWinnerUser) return undefined

    const duration = 3000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 }

    const randomInRange = (min, max) => Math.random() * (max - min) + min

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        clearInterval(interval)
        return
      }

      const particleCount = 50 * (timeLeft / duration)

      // Конфетти при победе
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#00FF7F', '#4EFFC6', '#FFD700']
      })
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#00FF7F', '#4EFFC6', '#FFD700']
      })
    }, 250)

    return () => clearInterval(interval)
  }, [isWinnerUser, isRoundParticipant, skipEffects])

  const formattedWin = Number(winAmount || 0).toLocaleString()
  const percentNumber = Number(winPercent)
  const formattedPercent = Number.isFinite(percentNumber)
    ? `${percentNumber.toFixed(2)}%`
    : '—'
  const percentToneClass = Number.isFinite(percentNumber) && percentNumber < 5
    ? 'bg-[#FFD166]/15 text-[#FFD166] border-[#FFD166]/40'
    : 'bg-accent-primary/15 text-accent-primary border-accent-primary/30'
  const statusLabel = isRoundParticipant
    ? isWinnerUser
      ? t('royale.winner.statusWin')
      : t('royale.winner.statusLose')
    : null
  const statusClassName = isWinnerUser
    ? 'border-accent-primary/60 bg-accent-primary/15 text-accent-primary'
    : 'border-red-400/40 bg-red-500/10 text-red-200'

  return (
    <motion.div
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#10161f] via-[#121a1c] to-[#0f1116] p-5 shadow-[0_0_24px_rgba(0,255,127,0.15)]"
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <div className="absolute -top-8 -right-6 h-24 w-24 rounded-full bg-accent-primary/15 blur-2xl" />
      <div className="absolute bottom-0 left-0 h-20 w-20 rounded-full bg-[#4DDCFF]/10 blur-2xl" />

      <div className="relative z-10 flex flex-col items-center text-center gap-4">
        {statusLabel && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${statusClassName}`}>
            {statusLabel}
          </span>
        )}
        <div>
          <div className="mt-3 flex flex-col items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-accent-primary/40 blur-lg" />
              <img
                src={winner.ava || `https://ui-avatars.com/api/?name=${winner.name}&background=00FF7F&color=0A0A0F`}
                alt={winner.name}
                className="relative w-20 h-20 rounded-full object-cover border border-white/20"
              />
            </div>
            <div>
              <p className="text-white text-xl font-semibold">{winner.name}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          <p className="text-xs text-text-secondary">{t('royale.winner.totalWin')}</p>
          <p className="inline-flex items-center gap-2 text-2xl font-bold text-white">
            <Gem className="w-5 h-5 text-accent-primary" />
            {formattedWin}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold border ${percentToneClass}`}>
          {formattedPercent}
        </span>
      </div>
    </motion.div>
  )
}

export default RoyaleWinnerCard
